import { useCallback } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  createExperimentSchema,
  type CreateExperimentFormValues,
} from "@/lib/validation/experiment-schema";
import { useCreateExperiment } from "@/hooks/use-experiments";

const PLATFORM_OPTIONS = [
  { value: "web", label: "Web" },
  { value: "mobile", label: "Mobile" },
  { value: "desktop", label: "Desktop" },
] as const;

interface CreateExperimentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateExperimentDialog({
  open,
  onOpenChange,
}: CreateExperimentDialogProps) {
  const createMutation = useCreateExperiment();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateExperimentFormValues>({
    resolver: zodResolver(createExperimentSchema),
    defaultValues: {
      key: "",
      name: "",
      description: "",
      variants: [
        { key: "control", weight: 5000 },
        { key: "variant_a", weight: 5000 },
      ],
      targeting: {
        newUsersOnly: false,
        platforms: [],
        startDate: undefined,
        endDate: undefined,
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "variants",
  });

  const variants = watch("variants");
  const totalWeight = variants?.reduce((sum, v) => sum + (v.weight || 0), 0) ?? 0;

  const targeting = watch("targeting");
  const selectedPlatforms = targeting?.platforms ?? [];

  const togglePlatform = useCallback(
    (platform: "web" | "mobile" | "desktop") => {
      const current = selectedPlatforms;
      const next = current.includes(platform)
        ? current.filter((p) => p !== platform)
        : [...current, platform];
      setValue("targeting.platforms", next, { shouldValidate: true });
    },
    [selectedPlatforms, setValue],
  );

  const onSubmit = useCallback(
    (data: CreateExperimentFormValues) => {
      const cleanedTargeting = cleanTargeting(data.targeting);

      createMutation.mutate(
        { ...data, targeting: cleanedTargeting },
        {
          onSuccess: () => {
            reset();
            onOpenChange(false);
          },
        },
      );
    },
    [createMutation, reset, onOpenChange],
  );

  const handleClose = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) reset();
      onOpenChange(isOpen);
    },
    [reset, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>새 실험 생성</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="key">실험 Key</Label>
            <Input
              id="key"
              placeholder="onboarding_survey_v1"
              {...register("key")}
            />
            {errors.key && (
              <p className="text-sm text-destructive">{errors.key.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">실험명</Label>
            <Input
              id="name"
              placeholder="온보딩 설문 A/B 테스트"
              {...register("name")}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">설명 (선택)</Label>
            <Textarea
              id="description"
              placeholder="실험에 대한 설명을 입력하세요"
              rows={2}
              {...register("description")}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Variants</Label>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs ${totalWeight === 10000 ? "text-emerald-600" : "text-destructive"}`}
                >
                  합계: {(totalWeight / 100).toFixed(1)}% ({totalWeight}/10000)
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ key: "", weight: 0 })}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  추가
                </Button>
              </div>
            </div>

            {fields.map((field, index) => (
              <div key={field.id} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    placeholder="variant key"
                    {...register(`variants.${index}.key`)}
                  />
                  {errors.variants?.[index]?.key && (
                    <p className="text-xs text-destructive mt-1">
                      {errors.variants[index].key.message}
                    </p>
                  )}
                </div>
                <div className="w-24">
                  <Controller
                    name={`variants.${index}.weight`}
                    control={control}
                    render={({ field: f }) => (
                      <Input
                        type="number"
                        placeholder="5000"
                        value={f.value}
                        onChange={(e) => f.onChange(Number(e.target.value) || 0)}
                      />
                    )}
                  />
                </div>
                <div className="w-12 text-center text-xs text-muted-foreground pt-2">
                  {variants?.[index]?.weight
                    ? `${(variants[index].weight / 100).toFixed(1)}%`
                    : "0%"}
                </div>
                {fields.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`variant ${index + 1} 삭제`}
                    title="variant 삭제"
                    onClick={() => remove(index)}
                    className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}

            {errors.variants?.root && (
              <p className="text-sm text-destructive">
                {errors.variants.root.message}
              </p>
            )}
            {errors.variants?.message && (
              <p className="text-sm text-destructive">
                {errors.variants.message}
              </p>
            )}
          </div>

          <Separator />

          <TargetingSection
            control={control}
            errors={errors}
            targeting={targeting}
            selectedPlatforms={selectedPlatforms}
            onTogglePlatform={togglePlatform}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
            >
              취소
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "생성 중..." : "생성"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TargetingSection({
  control,
  errors,
  targeting,
  selectedPlatforms,
  onTogglePlatform,
}: {
  control: ReturnType<typeof useForm<CreateExperimentFormValues>>["control"];
  errors: ReturnType<typeof useForm<CreateExperimentFormValues>>["formState"]["errors"];
  targeting: CreateExperimentFormValues["targeting"];
  selectedPlatforms: readonly string[];
  onTogglePlatform: (platform: "web" | "mobile" | "desktop") => void;
}) {
  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium">타겟팅 설정 (선택)</Label>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">실험 기간</Label>
        <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
          <span className="text-xs text-muted-foreground">시작일</span>
          <Controller
            name="targeting.startDate"
            control={control}
            render={({ field }) => (
              <DatePickerField
                value={field.value}
                onChange={field.onChange}
                placeholder="시작일 선택"
              />
            )}
          />
          <span className="text-xs text-muted-foreground">종료일</span>
          <Controller
            name="targeting.endDate"
            control={control}
            render={({ field }) => (
              <DatePickerField
                value={field.value}
                onChange={field.onChange}
                placeholder="종료일 선택"
                disableBefore={targeting?.startDate}
              />
            )}
          />
        </div>
        {errors.targeting?.startDate && (
          <p className="text-xs text-destructive">
            {errors.targeting.startDate.message}
          </p>
        )}
        {errors.targeting?.endDate && (
          <p className="text-xs text-destructive">
            {errors.targeting.endDate.message}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="text-xs text-muted-foreground">신규 유저만</Label>
          <p className="text-xs text-muted-foreground">
            시작일 이후 가입한 유저에게만 노출
          </p>
        </div>
        <Controller
          name="targeting.newUsersOnly"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">플랫폼 제한</Label>
        <div className="flex gap-2">
          {PLATFORM_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              type="button"
              variant={selectedPlatforms.includes(opt.value) ? "default" : "outline"}
              size="sm"
              onClick={() => onTogglePlatform(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          미선택 시 모든 플랫폼에 노출
        </p>
      </div>

      {/* TODO: 구독 상태별 필터 (무료/유료/학생) — SubscriptionStatus enum 연동 필요 */}
      {/* TODO: 가입 기간별 필터 (가입 후 N일 이내/이후) — userCreatedAt 기반 범위 조건 */}
      {/* TODO: 사용량 기반 필터 (작품 N개 이상 작성한 유저) */}
    </div>
  );
}

function DatePickerField({
  value,
  onChange,
  placeholder,
  disableBefore,
  disabled,
}: {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder: string;
  disableBefore?: Date;
  disabled?: boolean;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-3.5 w-3.5" />
          {value ? format(value, "yyyy.MM.dd") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          disabled={disableBefore ? (date) => date < disableBefore : undefined}
          weekStartsOn={0}
        />
      </PopoverContent>
    </Popover>
  );
}

function cleanTargeting(
  targeting: CreateExperimentFormValues["targeting"],
) {
  if (!targeting) return undefined;

  const { newUsersOnly, platforms, startDate, endDate } = targeting;
  const hasAnyTargeting =
    newUsersOnly || (platforms && platforms.length > 0) || startDate || endDate;

  if (!hasAnyTargeting) return undefined;

  return {
    ...(newUsersOnly ? { newUsersOnly: true } : {}),
    ...(platforms && platforms.length > 0 ? { platforms } : {}),
    ...(startDate ? { startDate: startDate.toISOString() } : {}),
    ...(endDate ? { endDate: endDate.toISOString() } : {}),
  };
}
